import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, RotateCcw, Save, XCircle } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'
import { useAdminDepartments } from '@/hooks/useAdminDepartments'
import type { Department, SlaRow, UrgentTier } from '@/types'
import UrgentRatesEditor, { urgentRowDrafts, urgentRowsPayload, urgentRowsProblem, type UrgentRowDraft } from '@/components/admin/UrgentRatesEditor'
import Card, { SectionLabel } from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import { Input } from '@/components/ui/Field'
import { Table, Row, Cell, EmptyRow } from '@/components/ui/Table'
import { useToast } from '@/components/ui/toast'
import LogoLoader from '@/components/ui/LogoLoader'

interface Draft {
  standard: string
  urgent: string
  /** '' = unlimited. */
  capacity: string
}

/**
 * The old TRACK system's maximum pieces per day for Apparel, shown under each
 * section as a starting point. TRACK had no QC process, so QC has no hint.
 */
const legacyCapacity: Record<string, number> = {
  Design: 480,
  Print: 800,
  'Heat Press': 800,
  Laser: 800,
  Tailor: 800,
}

const fromDepartment = (dept: Department): Draft => ({
  standard: String(dept.sla_days),
  urgent: String(dept.urgent_sla_days),
  capacity: dept.daily_capacity == null ? '' : String(dept.daily_capacity),
})

/** Working days in half-day steps, 0 to 60 - the rule the server holds too. */
function isHalfDays(value: string): boolean {
  const n = Number(value)
  return value.trim() !== '' && Number.isFinite(n) && n >= 0 && n <= 60 && Number.isInteger(n * 2)
}

const labelClass = 'mt-2 block text-2xs font-semibold uppercase tracking-wide text-muted'

export default function AdminSla() {
  const toast = useToast()
  const queryClient = useQueryClient()
  // Only the sections the admin has touched; everything else reads from the server.
  const [edits, setEdits] = useState<Record<number, Draft>>({})

  const departments = useAdminDepartments()

  const report = useQuery({
    queryKey: ['sla-report'],
    queryFn: async () => (await api.get<{ data: SlaRow[] }>('/admin/reports/sla')).data.data,
  })

  const rows = (departments.data?.departments ?? []).map((dept) => {
    const saved = fromDepartment(dept)
    const draft = edits[dept.id] ?? saved
    const slaOk = isHalfDays(draft.standard) && isHalfDays(draft.urgent)
    const problems = [
      !slaOk && 'SLA is in whole or half days, 0 to 60.',
      slaOk && Number(draft.urgent) > Number(draft.standard) && 'Urgent cannot be slower than standard.',
      draft.capacity.trim() !== '' &&
        !(Number.isInteger(Number(draft.capacity)) && Number(draft.capacity) >= 1) &&
        'Capacity is a whole number of pieces, or blank for unlimited.',
    ].filter((problem): problem is string => Boolean(problem))
    const dirty =
      Number(draft.standard) !== Number(saved.standard) ||
      Number(draft.urgent) !== Number(saved.urgent) ||
      draft.capacity.trim() !== saved.capacity

    return { dept, draft, problems, dirty }
  })

  const changed = rows.filter((row) => row.dirty)
  const invalid = rows.some((row) => row.problems.length > 0)

  // Worked out from what is on screen, so the effect of a half day shows
  // before saving: the chain rounds up to whole working days.
  const slasValid = rows.every((row) => isHalfDays(row.draft.standard) && isHalfDays(row.draft.urgent))
  const chainDays = (pick: (draft: Draft) => string) =>
    slasValid ? String(Math.ceil(rows.reduce((sum, row) => sum + Number(pick(row.draft)), 0))) : '...'

  const setField = (dept: Department, field: keyof Draft, value: string) =>
    setEdits((current) => ({ ...current, [dept.id]: { ...(current[dept.id] ?? fromDepartment(dept)), [field]: value } }))

  // One request for every changed section: the server saves all of them or
  // none, and checks each urgent SLA against its standard one.
  const save = useMutation({
    mutationFn: async () =>
      (
        await api.put<{ message: string }>('/admin/departments', {
          departments: changed.map(({ dept, draft }) => ({
            id: dept.id,
            sla_days: Number(draft.standard),
            urgent_sla_days: Number(draft.urgent),
            daily_capacity: draft.capacity.trim() === '' ? null : Number(draft.capacity),
          })),
        })
      ).data,
    onSuccess: async (res) => {
      await queryClient.invalidateQueries({ queryKey: ['admin-departments'] })
      setEdits({})
      queryClient.invalidateQueries({ queryKey: ['sla-report'] })
      queryClient.invalidateQueries({ queryKey: ['capacity'] })
      queryClient.invalidateQueries({ queryKey: ['section-load'] })
      toast.success('Sections saved', res.message)
    },
    onError: (err) => toast.error('Save failed', errorMessage(err)),
  })

  if (departments.isLoading) return <LogoLoader />

  return (
    <div>
      <PageHeader
        title="Sections: SLA and daily capacity"
        subtitle={`Each section is booked on the first working day after the previous one that still has room. SLAs are in whole or half days, and two half-day sections share a working day. With no capacity set, delivery due = production date + ${chainDays((d) => d.standard)} working days, or ${chainDays((d) => d.urgent)} when urgent.`}
      />

      <Card className="mb-5">
        <SectionLabel>Per section</SectionLabel>
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {rows.map(({ dept, draft, problems, dirty }) => (
            <div
              key={dept.id}
              className={cn('rounded-lg border p-3', dirty ? 'border-primary/60 bg-primary/5' : 'border-line')}
            >
              <p className="text-sm font-bold">
                {dept.sequence}. {dept.name}
              </p>
              {/* Its own line: six cards are narrow, and beside the name it overflowed. */}
              {dirty && <p className="text-2xs font-semibold text-primary">Changed</p>}
              <label htmlFor={`sla-${dept.id}`} className={labelClass}>
                Standard SLA (days)
              </label>
              <Input
                id={`sla-${dept.id}`}
                type="number"
                inputMode="decimal"
                min={0}
                max={60}
                step={0.5}
                value={draft.standard}
                onChange={(e) => setField(dept, 'standard', e.target.value)}
              />
              <label htmlFor={`urgent-${dept.id}`} className={labelClass}>
                Urgent SLA (days)
              </label>
              <Input
                id={`urgent-${dept.id}`}
                type="number"
                inputMode="decimal"
                min={0}
                max={60}
                step={0.5}
                value={draft.urgent}
                onChange={(e) => setField(dept, 'urgent', e.target.value)}
              />
              <label htmlFor={`capacity-${dept.id}`} className={labelClass}>
                Daily capacity (pcs)
              </label>
              <Input
                id={`capacity-${dept.id}`}
                type="number"
                inputMode="numeric"
                min={1}
                placeholder="Unlimited"
                value={draft.capacity}
                onChange={(e) => setField(dept, 'capacity', e.target.value)}
              />
              {legacyCapacity[dept.name] !== undefined && (
                <p className="mt-1 text-2xs text-muted">Old system: {legacyCapacity[dept.name]} pcs/day</p>
              )}
              {problems.map((problem) => (
                <p key={problem} className="mt-1 text-2xs font-semibold text-status-danger">
                  {problem}
                </p>
              ))}
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-col gap-3 border-t border-line pt-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted">
            Changes apply to new bookings; jobs already scheduled keep their dates until they are re-planned. "Old
            system" is TRACK's maximum for Apparel.
          </p>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {changed.length > 0 && (
              <span className="text-xs font-semibold text-primary">
                {changed.length} {changed.length === 1 ? 'section' : 'sections'} changed
              </span>
            )}
            <Button variant="ghost" disabled={changed.length === 0 || save.isPending} onClick={() => setEdits({})}>
              <RotateCcw className="size-4" /> Discard
            </Button>
            <Button disabled={changed.length === 0 || invalid} loading={save.isPending} onClick={() => save.mutate()}>
              <Save className="size-4" /> Save changes
            </Button>
          </div>
        </div>
      </Card>

      <StandardUrgentPrices />

      <SectionLabel>SLA vs actual (from QR scan timestamps)</SectionLabel>
      <Table
        columns={[
          'Department',
          'SLA (days)',
          'Jobs measured',
          'Avg actual (days)',
          'On track',
          'Urgent SLA (days)',
          'Urgent jobs',
          'Urgent avg (days)',
          'Urgent on track',
        ]}
      >
        {(report.data?.length ?? 0) === 0 && <EmptyRow colSpan={9}>No SLA data yet.</EmptyRow>}
        {report.data?.map((row) => (
          <Row key={row.department}>
            <Cell className="font-semibold">{row.department}</Cell>
            <Cell>{row.sla_days}</Cell>
            <Cell>{row.jobs_measured}</Cell>
            <Cell>{row.avg_actual_days ?? '-'}</Cell>
            <Cell><OnTrack value={row.on_track} /></Cell>
            <Cell>{row.urgent_sla_days}</Cell>
            <Cell>{row.urgent_jobs_measured}</Cell>
            <Cell>{row.avg_actual_urgent_days ?? '-'}</Cell>
            <Cell><OnTrack value={row.on_track_urgent} /></Cell>
          </Row>
        ))}
      </Table>
    </div>
  )
}

/** Rush work is graded against the urgent target, standard work against the standard one. */
function OnTrack({ value }: { value: boolean | null }) {
  if (value === null) return <span className="text-muted">no data</span>

  return value ? (
    <span className="inline-flex items-center gap-1 font-semibold text-status-success">
      <CheckCircle2 className="size-4" /> Yes
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 font-semibold text-status-danger">
      <XCircle className="size-4" /> Exceeding SLA
    </span>
  )
}

/**
 * The standard urgent price table (client 2026-10-06): every product uses it
 * unless its own prices are set in Edit product. Saved whole, like the
 * sections above: a row removed here is deleted.
 */
function StandardUrgentPrices() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const rates = useQuery({
    queryKey: ['admin-urgent-rates'],
    queryFn: async () => (await api.get<{ data: UrgentTier[] }>('/admin/urgent-rates')).data.data,
  })
  const [draft, setDraft] = useState<UrgentRowDraft[] | null>(null)
  const rows = draft ?? urgentRowDrafts(rates.data)

  const save = useMutation({
    mutationFn: async () =>
      (await api.put<{ data: UrgentTier[]; message?: string }>('/admin/urgent-rates', { rates: urgentRowsPayload(rows) })).data,
    onSuccess: (res) => {
      queryClient.setQueryData(['admin-urgent-rates'], res.data)
      // Storefront and product pages carry the rows that apply.
      queryClient.invalidateQueries({ queryKey: ['delivery-quote'] })
      setDraft(null)
      toast.success('Urgent prices saved', res.message)
    },
    onError: (err) => toast.error('Save failed', errorMessage(err)),
  })

  return (
    <Card className="mb-5">
      <SectionLabel>Urgent prices (all products)</SectionLabel>
      <p className="-mt-2 mb-3 text-xs text-muted">
        Charged when a delivery date is shorter than normal - the faster, the higher. A product can use its own prices
        instead (Edit product &gt; Urgent production). Leave the table empty to offer no urgent service by default.
      </p>
      {rates.isLoading ? (
        <p className="text-sm text-muted">Loading...</p>
      ) : (
        <UrgentRatesEditor rows={rows} onChange={setDraft} allowEmpty />
      )}
      <div className="mt-3 flex justify-end gap-2">
        {draft && (
          <Button variant="outline" onClick={() => setDraft(null)}>
            <RotateCcw className="size-4" /> Discard
          </Button>
        )}
        <Button
          loading={save.isPending}
          disabled={!draft || (rows.length > 0 && Boolean(urgentRowsProblem(rows)))}
          onClick={() => save.mutate()}
        >
          <Save className="size-4" /> Save urgent prices
        </Button>
      </div>
    </Card>
  )
}
